import type { QuoteDocument } from 'paintscout'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles } from '@paintscout/ui'
import type { DialogProps } from '@paintscout/ui'
import { DialogStackContext, useClientOptions } from '@paintscout/ui-business'
import React, { useState, useContext } from 'react'
import type { BulkOperation } from '@paintscout/util/builder'
import { generateBulkEditDetails, handleBulkOperations } from '@paintscout/util/builder'
import EditBulkDetailsForm from './EditBulkDetailsForm'
import EditBulkReviewDialog from './EditBulkReviewDialog'

export interface EditBulkDialogProps extends DialogProps {
  quote: QuoteDocument
  keys: string[]
  onConfirm?: (quote: QuoteDocument) => void
  onCancel?: () => void
}

const useStyles = makeStyles((_theme) => ({
  root: {}
}))

function EditBulkDialog(props: EditBulkDialogProps) {
  const { quote, keys, onConfirm, onCancel, ...baseDialogProps } = props
  const classes = useStyles(props)
  const { openDialog, dismissDialog } = useContext(DialogStackContext)
  const { options } = useClientOptions()
  const currentQuote = quote
  const [operationsList, setOperationsList] = useState<BulkOperation[]>([])

  // Generate bulk details to work with from keys selected
  const bulkDetails = generateBulkEditDetails({ quote, options, keys })

  // Maintain operationsList on update of form elements
  const handleUpdateOperations = (updatedOperationsList: BulkOperation[]) => {
    setOperationsList(updatedOperationsList)
  }

  // carry out any complete operations in operationsList on confirm
  const handleConfirm = () => {
    // Grab our bulk operations that have been fully filled out and can be completed
    const executableOperations = operationsList.filter(
      (bulkOperation) =>
        bulkOperation.newProduct && (bulkOperation.lineItems?.length > 0 || bulkOperation.substrates?.length > 0)
    )

    // Grab before->after details for review dialog
    const oldTotalMaterials = currentQuote.totals.materials
    const oldTotalPrice = currentQuote.totals.price

    // Carry out line item and substrate operations on quote
    const updatedQuote = handleBulkOperations(currentQuote, executableOperations, options)

    // Grab before->after details for review dialog
    const newTotalMaterials = updatedQuote.totals.materials
    const newTotalPrice = updatedQuote.totals.price

    // Before we return an updated quote, lets have a review screen to preview the changes that will take effect.
    openDialog(EditBulkReviewDialog, {
      oldTotalMaterials,
      oldTotalPrice,
      newTotalMaterials,
      newTotalPrice,
      executableOperations,
      onConfirm: () => {
        // After all updates applied, and reviewed return the quote to be updated/saved
        dismissDialog()
        onConfirm(updatedQuote)
      },
      onCancel: () => dismissDialog()
    })
  }

  const leftButton = (
    <Button onClick={onCancel} variant="textNoPadding">
      Cancel
    </Button>
  )

  return (
    <Dialog className={classes.root} {...baseDialogProps} fullWidth={true} maxWidth={'md'}>
      <DialogTitle>{`Edit Bulk Details`}</DialogTitle>
      <DialogContent>
        <EditBulkDetailsForm
          keys={keys}
          quote={quote}
          options={options}
          bulkDetails={bulkDetails}
          handleUpdateOperations={handleUpdateOperations}
        />
      </DialogContent>
      <DialogActions showBorder={true} leftButton={leftButton}>
        <Button onClick={handleConfirm} variant={'contained'}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditBulkDialog
